import React from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { Link } from 'react-router-dom'
import { fetchRecentPortalPayments, sendPortalPaymentReceipt } from '../../api'
import styles from './PortalPaymentsCard.module.css'

const REFRESH_INTERVAL_MS = 15000

const money = (amount, currency = 'USD') => new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency,
}).format(Number(amount || 0))

const paymentDate = (value, fallback = 'Not available') => {
  if (!value) return fallback
  const date = new Date(value)
  if (Number.isNaN(+date)) return fallback
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const STATUS_DETAILS = {
  awaiting_payment: { label: 'Awaiting payment', className: styles.statusAwaiting },
  processing: { label: 'Processing', className: styles.statusProcessing },
  paid: { label: 'Paid', className: styles.statusPaid },
  failed: { label: 'Failed', className: styles.statusFailed },
  expired: { label: 'Expired', className: styles.statusExpired },
  canceled: { label: 'Canceled', className: styles.statusCanceled },
}

const statusDetails = (payment) => STATUS_DETAILS[payment?.status] || STATUS_DETAILS.awaiting_payment

const receiptDetails = (payment) => {
  if (payment.receiptStatus === 'sent') return { label: 'Receipt sent', className: styles.deliverySent }
  if (payment.receiptStatus === 'failed') return { label: 'Receipt failed', className: styles.deliveryFailed }
  if (payment.receiptStatus === 'sending') return { label: 'Sending receipt', className: styles.deliverySending }
  return { label: 'Receipt pending', className: styles.deliveryPending }
}

const notificationName = (notification) => {
  const recipient = notification.audience === 'owner' ? 'Owner' : 'Customer'
  if (notification.eventType === 'payment_intent.processing') return `${recipient} processing update`
  if (notification.eventType === 'payment_intent.succeeded') return `${recipient} completion update`
  if (notification.eventType === 'payment_intent.payment_failed') return `${recipient} failure update`
  return `${recipient} payment update`
}

const notificationDelivery = (notification) => {
  if (notification.status === 'sent') return { label: 'Sent', className: styles.deliverySent }
  if (notification.status === 'failed') return { label: 'Failed', className: styles.deliveryFailed }
  return { label: 'Sending', className: styles.deliverySending }
}

const PortalPaymentsCard = () => {
  const [payments, setPayments] = React.useState([])
  const [selectedPayment, setSelectedPayment] = React.useState(null)
  const [ownerNotificationsConfigured, setOwnerNotificationsConfigured] = React.useState(true)
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [error, setError] = React.useState('')
  const [receiptActionError, setReceiptActionError] = React.useState('')
  const [sendingReceiptId, setSendingReceiptId] = React.useState('')
  const [refreshedAt, setRefreshedAt] = React.useState(null)

  const loadPayments = React.useCallback(async (background = false) => {
    if (background) setRefreshing(true)
    else setLoading(true)
    if (!background) setError('')
    try {
      const { data } = await fetchRecentPortalPayments(25)
      const nextPayments = Array.isArray(data?.payments) ? data.payments : []
      setPayments(nextPayments)
      setOwnerNotificationsConfigured(data?.ownerNotificationsConfigured !== false)
      setRefreshedAt(data?.refreshedAt || new Date().toISOString())
      setSelectedPayment((current) => {
        if (!current) return null
        return nextPayments.find((payment) => payment.id === current.id) || current
      })
    } catch (requestError) {
      if (!background) setError(requestError?.response?.data?.message || 'Unable to load portal payments.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  React.useEffect(() => {
    loadPayments()
    const refreshTimer = setInterval(() => loadPayments(true), REFRESH_INTERVAL_MS)
    return () => clearInterval(refreshTimer)
  }, [loadPayments])

  const updatePayment = React.useCallback((paymentId, updates) => {
    setPayments((current) => current.map((entry) => (
      entry.id === paymentId ? { ...entry, ...updates } : entry
    )))
    setSelectedPayment((current) => (
      current?.id === paymentId ? { ...current, ...updates } : current
    ))
  }, [])

  const handleSendReceipt = async (payment) => {
    setSendingReceiptId(payment.id)
    setReceiptActionError('')
    updatePayment(payment.id, { receiptStatus: 'sending', receiptError: null })
    try {
      const { data } = await sendPortalPaymentReceipt(payment.id)
      updatePayment(payment.id, data)
    } catch (requestError) {
      const message = requestError?.response?.data?.message || 'Unable to send the receipt.'
      setReceiptActionError(message)
      updatePayment(payment.id, { receiptStatus: 'failed', receiptError: message })
    } finally {
      setSendingReceiptId('')
    }
  }

  const paidPayments = payments.filter((payment) => payment.status === 'paid')
  const processingCount = payments.filter((payment) => payment.status === 'processing').length
  const paidTotal = paidPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
  const subheaderParts = []
  if (processingCount) subheaderParts.push(`${processingCount} processing`)
  if (paidPayments.length) subheaderParts.push(`${paidPayments.length} paid · ${money(paidTotal)}`)

  const selectedStatus = statusDetails(selectedPayment)
  const selectedReceipt = selectedPayment ? receiptDetails(selectedPayment) : null

  return (
    <>
      <Card variant="outlined" className={styles.card}>
        <CardHeader
          title="Portal Payments"
          subheader={subheaderParts.length ? subheaderParts.join(' • ') : 'Live ACH payment status'}
          action={(
            <div className={styles.refreshAction}>
              <span>{refreshing ? 'Updating…' : `Updated ${paymentDate(refreshedAt, 'just now')}`}</span>
              <Button size="small" onClick={() => loadPayments()} disabled={loading || refreshing}>Refresh</Button>
            </div>
          )}
        />
        <CardContent className={styles.cardContent}>
          {loading ? (
            <div className={styles.state}><CircularProgress size={24} /></div>
          ) : error ? (
            <div className={styles.state}>
              <Typography color="error" variant="body2">{error}</Typography>
              <Button size="small" onClick={() => loadPayments()}>Try again</Button>
            </div>
          ) : payments.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No portal payment links have been created yet.
            </Typography>
          ) : (
            <div className={styles.table}>
              <div className={styles.header}>
                <span>Customer</span>
                <span>Status</span>
                <span>Updated</span>
                <span>Amount</span>
              </div>
              {payments.map((payment) => {
                const status = statusDetails(payment)
                return (
                  <div
                    className={styles.row}
                    key={payment.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedPayment(payment)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setSelectedPayment(payment)
                      }
                    }}
                  >
                    <div className={styles.customer}>
                      <strong>{payment.customerName || 'Customer'}</strong>
                      <span>{payment.invoiceNumber ? `Invoice ${payment.invoiceNumber} • ` : ''}{payment.customerEmail || 'No customer email'}</span>
                    </div>
                    <div><span className={`${styles.statusBadge} ${status.className}`}>{status.label}</span></div>
                    <div className={styles.updated}>
                      <strong>{paymentDate(payment.statusUpdatedAt)}</strong>
                      <span>{payment.paymentMethod || 'ACH'}</span>
                    </div>
                    <strong className={styles.amount}>{money(payment.amount, payment.currency)}</strong>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selectedPayment)}
        onClose={() => setSelectedPayment(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="portal-payment-details-title"
      >
        {selectedPayment ? (
          <>
            <DialogTitle id="portal-payment-details-title">Payment details</DialogTitle>
            <DialogContent dividers>
              <div className={styles.detailHeading}>
                <div>
                  <span className={`${styles.statusBadge} ${selectedStatus.className}`}>{selectedStatus.label}</span>
                  <h3>{money(selectedPayment.amount, selectedPayment.currency)}</h3>
                  <p>{selectedPayment.customerName || 'Customer'}</p>
                </div>
                <Link className={styles.invoiceLink} to={`/invoice/${selectedPayment.invoiceId}`}>
                  View invoice
                </Link>
              </div>

              {selectedPayment.failureMessage ? (
                <div className={styles.failureMessage}>
                  <strong>Stripe status message</strong>
                  <span>{selectedPayment.failureMessage}</span>
                </div>
              ) : null}

              <div className={styles.detailGrid}>
                <div><span>Customer email</span><strong>{selectedPayment.customerEmail || 'Not available'}</strong></div>
                <div><span>Invoice</span><strong>{selectedPayment.invoiceNumber ? `#${selectedPayment.invoiceNumber}` : 'Invoice number hidden'}</strong></div>
                <div><span>Status updated</span><strong>{paymentDate(selectedPayment.statusUpdatedAt)}</strong></div>
                <div><span>Payment session started</span><strong>{paymentDate(selectedPayment.sessionStartedAt)}</strong></div>
                <div><span>Processing started</span><strong>{paymentDate(selectedPayment.processingAt)}</strong></div>
                <div><span>Completed</span><strong>{paymentDate(selectedPayment.paidAt)}</strong></div>
                <div className={styles.wideDetail}><span>Stripe payment reference</span><strong>{selectedPayment.stripePaymentIntentId || 'Not available'}</strong></div>
              </div>

              {selectedPayment.items?.length ? (
                <section className={styles.detailSection}>
                  <h4>Invoice items</h4>
                  <div className={styles.itemList}>
                    {selectedPayment.items.map((item, index) => (
                      <div key={`${item.modelNumber}-${index}`}>
                        <span>{item.modelNumber} × {item.quantity}</span>
                        <strong>{money(item.lineTotal, selectedPayment.currency)}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              <section className={styles.detailSection}>
                <h4>Email delivery</h4>
                {!ownerNotificationsConfigured ? (
                  <div className={styles.configurationWarning}>
                    Owner emails are disabled until <code>PAYMENT_OWNER_EMAILS</code> is configured on the server.
                  </div>
                ) : null}
                {selectedPayment.notifications?.length ? (
                  <div className={styles.notificationList}>
                    {selectedPayment.notifications.map((notification, index) => {
                      const delivery = notificationDelivery(notification)
                      return (
                        <div key={`${notification.stripeEventId}-${notification.audience}-${index}`}>
                          <span>
                            <strong>{notificationName(notification)}</strong>
                            <small>{notification.sentAt ? paymentDate(notification.sentAt) : (notification.error || 'Delivery pending')}</small>
                          </span>
                          <span className={`${styles.deliveryBadge} ${delivery.className}`}>{delivery.label}</span>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <Typography variant="body2" color="textSecondary">No status emails recorded yet.</Typography>
                )}

                {selectedPayment.status === 'paid' ? (
                  <div className={styles.receiptRow}>
                    <div>
                      <strong>Customer receipt</strong>
                      <span>{selectedPayment.receiptSentAt ? paymentDate(selectedPayment.receiptSentAt) : (selectedPayment.receiptError || 'Awaiting delivery')}</span>
                    </div>
                    <div>
                      <span className={`${styles.deliveryBadge} ${selectedReceipt.className}`}>{selectedReceipt.label}</span>
                      {!['sent', 'sending'].includes(selectedPayment.receiptStatus) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={sendingReceiptId === selectedPayment.id}
                          onClick={() => handleSendReceipt(selectedPayment)}
                        >
                          {selectedPayment.receiptStatus === 'failed' ? 'Retry receipt' : 'Send receipt'}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
                {receiptActionError ? <Typography className={styles.actionError} color="error" variant="body2">{receiptActionError}</Typography> : null}
              </section>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedPayment(null)}>Close</Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </>
  )
}

export default PortalPaymentsCard
